<template>
  <div :class="{ 'input_group': true, 'input_error': props.error, 'disabled': disabled }">
    <Editor :id="name" :name="name" :tag="'textarea'" :init="configuration" :initial-value="modelValue" class="input_field" />
    <span v-if="props.error" class="error_message">{{ props.error }}</span>
  </div>
</template>

<script setup>
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/image";
import "tinymce/plugins/paste";
import "tinymce/plugins/wordcount";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin.css";
import Editor from '@tinymce/tinymce-vue';
import {defineProps, defineEmits, ref, inject} from 'vue'
import {useArticleStore} from "@/stores/ArticleStore";
import {useAuthStore} from "@/stores/AuthStore";

const authStore = useAuthStore();
const articleStore = useArticleStore();
const loader = inject('$loader');

const props = defineProps({
  name: String,
  placeholder: String,
  modelValue: {
    type: String,
    default: '<p>'
  },
  disabled: Boolean,
  error: String,
  idSite: String
})

const emit = defineEmits(['update:modelValue']);

const bodyColor = ref(authStore.user.dark ? '#b5b9d0' : '#1e2a36');

const configuration = {
  language: 'pt_BR',
  object_resizing: 'img',
  language_url: '/tinymce_lang.js',
  branding: false,
  block_formats: 'Paragráfo=p;Título=h2',
  content_style: "@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@200;400;500;600&family=Roboto&display=swap'); body { font-family: Poppins, Helvetica, Arial, sans-serif; font-size: 16px; font-weight: 400; color: " + bodyColor.value + "; } h2 { font-size: 24px !important; font-weight: 400; line-height: 30px; } p { font-size: 14px !important; }",
  menubar: false,
  paste_as_text: true,
  plugins: 'lists link image paste',
  toolbar: 'formatselect bold italic removeformat numlist bullist link imageUpload | instagram facebook twitter youtube tiktok',
  setup(editor) {

    editor.on('change', function () {
      emit('update:modelValue', editor.getContent());
    });

    editor.on('keyup', function () {
      emit('update:modelValue', editor.getContent());
    });

    editor.ui.registry.addButton('instagram', {
      icon: 'instagram',
      tooltip: 'Inserir post do Instagram',
      onAction: function () {
        editor.windowManager.open({
          title: 'Inserir post do Instagram',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'url',
                label: 'Url',
                flex: true
              }
            ]
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancelar'
            },
            {
              type: 'submit',
              text: 'Inserir',
              primary: true
            }
          ],
          onSubmit: function (api) {
            const data = api.getData();
            if (data.url) {
              editor.insertContent('[embed]' + data.url + '[/embed]');
            }
            api.close();
          }
        });
      }
    });

    editor.ui.registry.addButton('facebook', {
      icon: 'facebook',
      tooltip: 'Inserir post do Facebook',
      onAction: function () {
        editor.windowManager.open({
          title: 'Inserir post do Facebook',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'url',
                label: 'Url',
                flex: true
              }
            ]
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancelar'
            },
            {
              type: 'submit',
              text: 'Inserir',
              primary: true
            }
          ],
          onSubmit: function (api) {
            const data = api.getData();
            if (data.url) {
              editor.insertContent('[embed]' + data.url + '[/embed]');
            }
            api.close();
          }
        });
      }
    });

    editor.ui.registry.addButton('twitter', {
      icon: 'twitter',
      tooltip: 'Inserir post do X',
      onAction: function () {
        editor.windowManager.open({
          title: 'Inserir post do X',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'url',
                label: 'Url',
                flex: true
              }
            ]
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancelar'
            },
            {
              type: 'submit',
              text: 'Inserir',
              primary: true
            }
          ],
          onSubmit: function (api) {
            const data = api.getData();
            if (data.url) {
              editor.insertContent('[embed]' + data.url + '[/embed]');
            }
            api.close();
          }
        });
      }
    });

    editor.ui.registry.addButton('youtube', {
      icon: 'youtube',
      tooltip: 'Inserir vídeo do Youtube',
      onAction: function () {
        editor.windowManager.open({
          title: 'Inserir post do Youtube',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'url',
                label: 'Url',
                flex: true
              }
            ]
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancelar'
            },
            {
              type: 'submit',
              text: 'Inserir',
              primary: true
            }
          ],
          onSubmit: function (api) {
            const data = api.getData();
            if (data.url) {
              editor.insertContent('[embed]' + data.url + '[/embed]');
            }
            api.close();
          }
        });
      }
    });

    editor.ui.registry.addButton('tiktok', {
      icon: 'tiktok',
      tooltip: 'Inserir vídeo do Tiktok',
      onAction: function () {
        editor.windowManager.open({
          title: 'Inserir post do Tiktok',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'url',
                label: 'Url',
                flex: true
              }
            ]
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancelar'
            },
            {
              type: 'submit',
              text: 'Inserir',
              primary: true
            }
          ],
          onSubmit: function (api) {
            const data = api.getData();
            if (data.url) {
              editor.insertContent('[embed]' + data.url + '[/embed]');
            }
            api.close();
          }
        });
      }
    });

    editor.ui.registry.addButton('imageUpload', {
      icon: 'image',
      tooltip: 'Inserir imagem',
      onAction: function () {
        editor.windowManager.open({
          title: 'Inserir imagem',
          body: {
            type: 'panel',
            items: [
              {
                type: 'htmlpanel',
                html: `
                <div class="input_group">
                  <button type="button" id="image-upload-trigger" class="image_upload" onClick="window.openFileSelectorAndUpload()">
                    <h4 id="file-selected-status">Selecionar arquivo</h4>
                    <span id="file-selected-name">Tamanho máximo: 2 MB</span>
                  </button>
                  <input type="file" id="image-upload-input" class="file_input" accept="image/*" style="display: none;" />
                </div>
              `
              }
            ]
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancelar'
            },
            {
              type: 'submit',
              text: 'Inserir',
              primary: true
            }
          ],
          onSubmit: async function (api) {
            const fileInput = document.getElementById('image-upload-input');

            if (fileInput.files.length > 0) {
              const file = fileInput.files[0];

              loader.show();

              try {
                const formResult = await articleStore.uploadAttachment({image: file, id_site: props.idSite});

                if (formResult.success) {
                  editor.insertContent('<img src="' + formResult.image + '" />');
                } else {
                  console.error('Erro no upload da imagem');
                }
              } catch (error) {
                console.error('Erro no upload da imagem', error);
              }

              loader.hide();
              api.close();
            }
          }
        });
      }
    });

    editor.ui.registry.addIcon('bold', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M0 64C0 46.3 14.3 32 32 32H80 96 224c70.7 0 128 57.3 128 128c0 31.3-11.3 60.1-30 82.3c37.1 22.4 62 63.1 62 109.7c0 70.7-57.3 128-128 128H96 80 32c-17.7 0-32-14.3-32-32s14.3-32 32-32H48V256 96H32C14.3 96 0 81.7 0 64zM224 224c35.3 0 64-28.7 64-64s-28.7-64-64-64H112V224H224zM112 288V416H256c35.3 0 64-28.7 64-64s-28.7-64-64-64H224 112z"/></svg>');
    editor.ui.registry.addIcon('italic', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M128 64c0-17.7 14.3-32 32-32H352c17.7 0 32 14.3 32 32s-14.3 32-32 32H293.3L160 416h64c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H90.7L224 96H160c-17.7 0-32-14.3-32-32z"/></svg>');
    editor.ui.registry.addIcon('ordered-list', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M40 48C26.7 48 16 58.7 16 72v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V72c0-13.3-10.7-24-24-24H40zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H192zM16 232v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V232c0-13.3-10.7-24-24-24H40c-13.3 0-24 10.7-24 24zM40 368c-13.3 0-24 10.7-24 24v48c0 13.3 10.7 24 24 24H88c13.3 0 24-10.7 24-24V392c0-13.3-10.7-24-24-24H40z"/></svg>');
    editor.ui.registry.addIcon('unordered-list', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M24 56c0-13.3 10.7-24 24-24H80c13.3 0 24 10.7 24 24V176h16c13.3 0 24 10.7 24 24s-10.7 24-24 24H40c-13.3 0-24-10.7-24-24s10.7-24 24-24H56V80H48C34.7 80 24 69.3 24 56zM86.7 341.2c-6.5-7.4-18.3-6.9-24 1.2L51.5 357.9c-7.7 10.8-22.7 13.3-33.5 5.6s-13.3-22.7-5.6-33.5l11.1-15.6c23.7-33.2 72.3-35.6 99.2-4.9c21.3 24.4 20.8 60.9-1.1 84.7L86.8 432H120c13.3 0 24 10.7 24 24s-10.7 24-24 24H32c-9.5 0-18.2-5.6-22-14.4s-2.1-18.9 4.3-25.9l72-78c5.3-5.8 5.4-14.6 .3-20.5zM224 64H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H224c-17.7 0-32-14.3-32-32s14.3-32 32-32zm0 160H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H224c-17.7 0-32-14.3-32-32s14.3-32 32-32zm0 160H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H224c-17.7 0-32-14.3-32-32s14.3-32 32-32z"/></svg>');
    editor.ui.registry.addIcon('link', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z"/></svg>');
    editor.ui.registry.addIcon('image', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M448 80c8.8 0 16 7.2 16 16V415.8l-5-6.5-136-176c-4.5-5.9-11.6-9.3-19-9.3s-14.4 3.4-19 9.3L202 340.7l-30.5-42.7C167 291.7 159.8 288 152 288s-15 3.7-19.5 10.1l-80 112L48 416.3l0-.3V96c0-8.8 7.2-16 16-16H448zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm80 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z"/></svg>');
    editor.ui.registry.addIcon('instagram', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>');
    editor.ui.registry.addIcon('facebook', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z"/></svg>');
    editor.ui.registry.addIcon('twitter', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"/></svg>');
    editor.ui.registry.addIcon('youtube', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6-11.4 42.9-11.4 132.3-11.4 132.3s0 89.4 11.4 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8 11.4-42.9 11.4-132.3 11.4-132.3s0-89.4-11.4-132.3zm-317.5 213.5V175.2l142.7 81.2-142.7 81.2z"/></svg>');
    editor.ui.registry.addIcon('tiktok', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M448 209.9a210.1 210.1 0 0 1 -122.8-39.3V349.4A162.6 162.6 0 1 1 185 188.3V278.2a74.6 74.6 0 1 0 52.2 71.2V0l88 0a121.2 121.2 0 0 0 1.9 22.2h0A122.2 122.2 0 0 0 381 102.4a121.4 121.4 0 0 0 67 20.1z"/></svg>');
    editor.ui.registry.addIcon('remove-formatting', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M290.7 57.4L57.4 290.7c-25 25-25 65.5 0 90.5l80 80c12 12 28.3 18.7 45.3 18.7H288h9.4H512c17.7 0 32-14.3 32-32s-14.3-32-32-32H387.9L518.6 285.3c25-25 25-65.5 0-90.5L381.3 57.4c-25-25-65.5-25-90.5 0zM297.4 416H288l-105.4 0-80-80L227.3 211.3 364.7 348.7 297.4 416z"/></svg>');
  },
};

window.openFileSelectorAndUpload = function () {
  const fileInput = document.getElementById('image-upload-input');
  const fileLegend = document.getElementById('file-selected-status');
  const fileName = document.getElementById('file-selected-name');

  fileInput.onchange = e => {
    const file = e.target.files[0];
    if (file) {
      fileLegend.innerText = 'Arquivo selecionado';
      fileName.innerText = file.name;
    }
  };

  fileInput.click();
}

</script>
<style scoped>
.input_group {
  display: flex;
  flex-direction: column;
}

.input_group label {
  margin-bottom: 4px;
  font-weight: 500;
  text-align: left;
}

.input_field {
  padding: 10px;
  border: 1px solid #d7d7d7;
  border-radius: 4px !important;
  font-size: 15px;
  height: 700px;
}

.dark .input_field {
  background-color: #232932 !important;
  border-color: #394251 !important;
  color: #b5b9d0 !important;
}

.dark .input_field:focus {
  box-shadow: 0px 0px 0px 2px #655582;
}

.input_field:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.input_field:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.dark .input_field:disabled {
  background-color: #000;
  opacity: 1;
}

.error_message {
  color: #df2b2b;
  font-weight: 500;
  margin-top: 4px;
}

.input_group.disabled {
  overflow: hidden;
}
</style>


<style>
.tox .tox-tbtn svg {
  height: 14px;
}

.tox-tinymce {
  border-radius: 6px;
}

.tox .tox-tbtn {
  border-radius: 2px;
}

.tox-dialog-wrap__backdrop {
  background: rgba(0, 0, 0, 0.5) !important;
  z-index: 9999;
}

.tox .tox-dialog {
  max-width: 400px;
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.2);
  z-index: 99999;
  border-radius: 6px;
}

.tox .tox-label,
.tox .tox-toolbar-label {
  margin-top: 5px;
  margin-bottom: 5px;
}

.tox .tox-textfield {
  padding: 10px;
  border-radius: 4px;
}

.tox .tox-listboxfield .tox-listbox--select {
  min-height: 46px !important;
  border-radius: 4px;
}

.tox .tox-dialog__header .tox-button {
  margin-right: -10px;
}

.tox-dialog__footer-end .tox-button {
  background-color: #583a8e;
}

.tox-dialog__footer-end .tox-button:hover,
.tox-dialog__footer-end .tox-button:active {
  background-color: #492f79 !important;
}

.tox-dialog__footer-end .tox-button--secondary {
  background-color: #f0f0f0;
}

.tox-dialog__footer-end .tox-button--secondary:hover,
.tox-dialog__footer-end .tox-button--secondary:active {
  background-color: #dedede !important;
}

.tox-textarea {
  padding: 10px;
  border: 1px solid #d7d7d7;
  border-radius: 4px !important;
  font-size: 14px;
  font-weight: 500;
  min-height: 200px !important;
}

.tox .tox-tbtn__select-label {
  font-weight: 500;
}

.tox .tox-dialog__footer {
  border-top: 0;
  padding-top: 0;
  padding-bottom: 15px;
}


.input_error .tox-tinymce {
  border: 1px #df2b2b solid;
  box-shadow: 0px 0px 0px 3px rgb(255 136 136 / 30%);
}

.tox .tox-toolbar__primary {
  background: #fff !important;
  border-bottom: 1px #e1e1e1 solid;
}

.tox .tox-tbtn--bespoke .tox-tbtn__select-label {
  width: 70px;
}

.tox .tox-tbtn__select-chevron {
  margin-top: 6px;
}

.tox .image_upload {
  height: 90px;
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  cursor: pointer;
  border: 0;
  width: 100%;
  border: 2px #ccc dashed;
}

.tox .image_upload:hover {
  background-color: #f1f1f1;
}

.tox .image_upload h4 {
  font-size: 16px;
  margin-bottom: 0;
}

.tox .image_upload span {
  font-size: 14px;
  font-weight: 500;
  color: #7f7f7f;
  max-width: 290px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.input_group.disabled .tox .tox-edit-area__iframe {
  background: #e9ecef !important;
}

.input_group.disabled .tox .tox-toolbar__primary {
  background: #e9ecef !important;
}

.dark .input_group.disabled .tox .tox-edit-area__iframe {
  background: #232932 !important;
}

.dark .input_group.disabled .tox .tox-toolbar__primary {
  background: #232932 !important;
}

.input_group.disabled .tox::before {
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 2;
}

.dark .tox .tox-toolbar-overlord {
  background-color: #232932 !important;
  border-color: #394251 !important;
  color: #b5b9d0 !important;
}

.dark .tox .tox-edit-area__iframe {
  background-color: #232932 !important;
}

.dark .tox .tox-toolbar__primary {
  background-color: #232932 !important;
  border-color: #394251 !important;
}

.dark .tox .tox-statusbar {
  background-color: #232932 !important;
  border-color: #394251 !important;
  color: #b5b9d0 !important;
}

.dark .tox .tox-statusbar__path-item,
.dark .tox .tox-tbtn {
  color: #333945 !important;
}

.dark .tox .tox-tbtn--enabled,
.dark .tox .tox-tbtn--enabled:hover,
.dark .tox .tox-tbtn:focus {
  background-color: #333945 !important;
  color: #b5b9d0 !important;
}

.dark .tox .tox-tbtn--select {
  color: #b5b9d0 !important;
}

.dark .tox .tox-tbtn svg {
  fill: #b5b9d0 !important;
}

.dark .tox:not([dir=rtl]) .tox-toolbar__group:not(:last-of-type) {
  border-color: #394251;
}

.dark .tox-tinymce {
  border-color: #394251;
}

.dark .tox .tox-tbtn:hover {
  background-color: #383f4b !important;
}

.dark .tox .tox-menu {
  background-color: #232932;
  color: #b5b9d0;
  border-color: #394251;
}

.dark .tox .tox-collection--list .tox-collection__item--active {
  background-color: #232932;
}

.dark .tox .tox-collection--list .tox-collection__item--active:hover {
  background-color: #2a323d;
}

.dark .tox .tox-collection--list .tox-collection__item--enabled {
  background-color: #2a323d;
}
</style>